import React from "react";

import { Table as AntTable, TableProps as AntTableProps } from "antd";
import { TFunction } from "i18next";

import { camelizeString } from "utils";

type TableProps<T extends object> = Omit<AntTableProps, "dataSource"> & {
  data: T[];
  t?: TFunction | undefined;
  hideHeader?: boolean;
  showSizeChanger?: boolean;
  setEqualWidthColumns?: boolean;
};

export const Table = function <T extends object>({
  className,
  data,
  loading,
  t,
  hideHeader,
  pagination,
  expandable,
  columns,
  showSizeChanger = true,
  setEqualWidthColumns = false,
  scroll,
  sticky,
  rowKey,
  ...rest
}: TableProps<T>) {
  const generateColumns = (data: object[], t?: TFunction) => {
    if (columns) {
      if (setEqualWidthColumns) {
        const totalColumns = columns.length - 1;
        const width = `${100 / totalColumns}%`;
        return columns.map((col, idx) => (idx === 0 ? col : { ...col, width }));
      }
      return columns;
    }

    const result: object[] = [];

    if (data?.length !== 0) {
      Object.keys(data[0]).forEach((item) => {
        const key = camelizeString(item);
        if (item !== "expandable" && item !== "key" && item !== "className") {
          result.push({
            title: item !== " " && t ? t(`table.${key}`) : item,
            dataIndex: item,
            key: key,
            className: "paragraph-xs-bold",
          });
        }
      });
    }

    return result;
  };

  return (
    <AntTable
      dataSource={data}
      columns={generateColumns(data, t)}
      className={`app-table ${className}`}
      pagination={
        pagination
          ? {
              ...pagination,
              hideOnSinglePage: true,
              position: ["bottomLeft"],
              showSizeChanger: showSizeChanger,
              responsive: true,
              ...(t && {
                locale: { items_per_page: `/ ${t("table.page")}` },
              }),
            }
          : false
      }
      expandable={{
        showExpandColumn: false,
        expandRowByClick: true,
        rowExpandable: (item) => item.expandable,
        ...expandable,
      }}
      loading={loading}
      showHeader={!hideHeader}
      locale={{ emptyText: " " }}
      scroll={scroll}
      sticky={sticky}
      rowKey={rowKey}
      {...rest}
    />
  );
};
